<template>
  <div id="manage-details" class="fill-height align-start">
    <v-row class="g-container" no-gutters>
      <v-col cols="12">
        <h2 class="ma-0 text-center">{{ $route.meta.title }}</h2>
      </v-col>

      <v-row no-gutters>
        <v-col cols="12">
          <v-card
            class="p-update-card mx-auto rounded-lg pa-4 px-md-10 py-md-6"
          >
            <v-form ref="page-content-form" disabled @submit.prevent>
              <v-alert
                dense
                text
                outlined
                border="left"
                elevation="1"
                type="info"
              >
                <div class="text-center">
                  To update <strong>Business Details & Licenses</strong> please
                  contact us at
                  <a
                    class="i-a-link"
                    :href="'mailto:' + supportEmail"
                    target="_blank"
                    >{{ supportEmail }}</a
                  >
                </div>
              </v-alert>

              <v-divider class="mt-6 mx-0" horizontal></v-divider>

              <v-card-text class="px-0 pt-0 pb-3 pb-md-6">
                <span class="g-heading-s g-bold">Business Details</span>
              </v-card-text>

              <v-row>
                <v-col class="py-0" cols="12" md="6">
                  <v-text-field
                    dense
                    v-model="mutableSupplier.name"
                    label="Supplier Trading Name"
                    outlined
                    background-color="#F0F0F0"
                    required
                  >
                  </v-text-field>
                </v-col>

                <v-col class="py-0" cols="12" md="6">
                  <v-text-field
                    dense
                    v-model="mutableSupplier.legalName"
                    label="Business Name"
                    outlined
                    background-color="#F0F0F0"
                    required
                  >
                  </v-text-field>
                </v-col>

                <v-col class="py-0" cols="12" md="6">
                  <v-text-field
                    dense
                    v-model="mutableSupplier.abn"
                    label="Business ABN"
                    hint="At least 11 digits"
                    outlined
                    background-color="#F0F0F0"
                    required
                  >
                  </v-text-field>
                </v-col>

                <v-col class="py-0" cols="12" md="6">
                  <v-text-field
                    dense
                    v-model="mutableSupplier.acn"
                    label="Business ACN"
                    background-color="#F0F0F0"
                    disabled
                    outlined
                  ></v-text-field>
                </v-col>

                <v-col class="py-0" cols="12" md="6">
                  <v-text-field
                    dense
                    v-model="mutableSupplier.businessContact.firstName"
                    label="Business Contact First Name"
                    outlined
                    background-color="#F0F0F0"
                    required
                  >
                  </v-text-field>
                </v-col>

                <v-col class="py-0" cols="12" md="6">
                  <v-text-field
                    dense
                    v-model="mutableSupplier.businessContact.lastName"
                    label="Business Contact Last Name"
                    outlined
                    background-color="#F0F0F0"
                    required
                  >
                  </v-text-field>
                </v-col>

                <v-col class="py-0" cols="12" md="6">
                  <v-text-field
                    dense
                    v-model="mutableSupplier.businessContact.email"
                    label="Business Contact Email"
                    outlined
                    background-color="#F0F0F0"
                    required
                  >
                  </v-text-field>
                </v-col>
                <v-col class="py-0" cols="12" md="6">
                  <v-text-field
                    dense
                    :value="supplier?.externalAccountCode || ''"
                    label="Supplier ID"
                    outlined
                    background-color="#F0F0F0"
                    readonly
                    disabled
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-divider class="mx-0" horizontal></v-divider>

              <v-card-text class="px-0 pt-0 pb-3 pb-md-6">
                <span class="g-heading-s g-bold">Licences</span>
              </v-card-text>

              <v-row>
                <v-col class="py-0" cols="12" md="6">
                  <v-text-field
                    dense
                    outlined
                    background-color="#F0F0F0"
                    counter="30"
                    maxlength="30"
                    label="Liquor Licence (Optional)"
                    v-model="mutableSupplier.liquorLicence"
                  >
                  </v-text-field>
                </v-col>
              </v-row>
            </v-form>
          </v-card>
          <v-card
            class="p-update-card mx-auto rounded-lg mt-2 mt-md-4 pb-0 pa-4 px-md-10 pt-md-6 mb-8"
          >
            <v-form
              ref="page-content-form"
              v-model="formState"
              :disabled="!editForm"
              @submit.prevent
            >
              <template v-if="!userIsAllowedToEdit">
                <v-alert
                  dense
                  text
                  outlined
                  border="left"
                  elevation="1"
                  type="info"
                >
                  <div class="text-center">
                    Contact <strong>Account Owner</strong> to update details
                  </div>
                </v-alert>

                <v-divider class="mt-6 mx-0" horizontal></v-divider>
              </template>

              <v-card-text class="px-0 pt-0 pb-3 pb-md-6">
                <span class="g-heading-s g-bold"
                  >Business / Billing Address</span
                >
              </v-card-text>

              <v-row>
                <v-col class="py-0" cols="12">
                  <div class="i-address-finder-div">
                    <v-btn
                      text
                      class="i-btn-link-secondary px-2 ml-auto mt-n6"
                      :disabled="!editForm"
                      @click="handleManualBillingAddress()"
                    >
                      {{
                        manualBillingAddress
                          ? 'Find Address'
                          : 'Cannot Find Address'
                      }}
                    </v-btn>
                  </div>

                  <template v-if="manualBillingAddress">
                    <v-text-field
                      dense
                      v-model="mutableSupplier.billingAddress.line1"
                      outlined
                      required
                      background-color="#F0F0F0"
                      label="* Street Number and Address (Required)"
                      :rules="[
                        (v) => !!v || 'Street Number and Address required'
                      ]"
                    ></v-text-field>
                  </template>

                  <template v-else>
                    <flewid-text-field
                      :value="displayBillingAddress"
                      outlined
                      required
                      background-color="#F0F0F0"
                      label="* Street Number and Address (Required)"
                      enable-address-auto-suggest
                      :rules="[
                        (v) => !!v || 'Street Number and Address required'
                      ]"
                      @placechanged="onBillingAddressPlaceChanged"
                    />
                  </template>
                </v-col>

                <template v-if="manualBillingAddress">
                  <v-col class="py-0" cols="12" md="6">
                    <v-text-field
                      dense
                      v-model="mutableSupplier.billingAddress.suburb"
                      outlined
                      required
                      background-color="#F0F0F0"
                      label="* Suburb (Required)"
                      :disabled="!manualBillingAddress"
                      :rules="[(v) => !!v || 'Suburb required']"
                    ></v-text-field>
                  </v-col>

                  <v-col class="py-0" cols="12" md="6">
                    <v-select
                      attach
                      background-color="#F0F0F0"
                      dense
                      :disabled="!manualBillingAddress"
                      :items="ausStates"
                      label="* State (Required)"
                      outlined
                      required
                      :rules="[(v) => !!v || 'Delivery State required']"
                      v-model="mutableSupplier.billingAddress.state"
                    ></v-select>
                  </v-col>

                  <v-col class="py-0" cols="12" md="6">
                    <v-text-field
                      dense
                      v-model="mutableSupplier.billingAddress.postCode"
                      outlined
                      required
                      background-color="#F0F0F0"
                      label="* Postcode (Required)"
                      :disabled="!manualBillingAddress"
                      :rules="rulesPostCode"
                    ></v-text-field>
                  </v-col>

                  <v-col class="py-0" cols="12" md="6">
                    <v-text-field
                      dense
                      v-model="mutableSupplier.billingAddress.country"
                      background-color="#F0F0F0"
                      label="* Country (Required)"
                      disabled
                      outlined
                      required
                      :rules="[(v) => !!v || 'Country required']"
                    ></v-text-field>
                  </v-col>
                </template>

                <v-col class="py-0" cols="12" md="6">
                  <v-text-field
                    dense
                    v-model="mutableSupplier.billingAddress.mobile"
                    outlined
                    required
                    background-color="#F0F0F0"
                    label="* Mobile or Landline (Required)"
                    hint="Add Area Code if Landline"
                    :rules="rulesPhone"
                  ></v-text-field>
                </v-col>
              </v-row>

              <template v-if="userIsAllowedToEdit">
                <v-card-actions
                  class="i-action-sticky-bottom py-4 py-md-6 pa-0 justify-center justify-md-end"
                >
                  <template v-if="!editForm">
                    <v-btn
                      rounded
                      class="i-btn-block-secondary"
                      @click="editForm = !editForm"
                    >
                      Edit details
                    </v-btn>
                  </template>
                  <template v-else>
                    <v-btn
                      rounded
                      text
                      class="i-btn-text-black"
                      @click="resetForm()"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      rounded
                      class="i-btn-block-secondary"
                      :loading="updating"
                      :disabled="!validDetailsForm"
                      @click="submitForm()"
                    >
                      Confirm
                    </v-btn>
                  </template>
                </v-card-actions>
              </template>
            </v-form>
          </v-card>
        </v-col>
      </v-row>
    </v-row>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters, mapState } from 'vuex'
import { updateProfile } from '@/services/profile'
import FieldValidators from '@/utils/FieldValidators'
import FlewidTextField from '@/components/FlewidTextField'

export default {
  components: {
    FlewidTextField
  },

  data() {
    return {
      ...FieldValidators,
      editForm: false,
      formState: false,
      updating: false,
      displayBillingAddress: null,
      manualBillingAddress: false,
      ausStates: ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'],
      mutableSupplier: {
        accountContact: {},
        businessContact: {},
        billingAddress: {}
      }
    }
  },

  computed: {
    ...mapGetters('preferences', ['isOwnerUser']),
    ...mapGetters('tenant', ['supportEmail']),
    ...mapState('preferences', ['selectedSupplierId']),
    ...mapState('profile', { supplier: 'info' }),

    isNewData() {
      return !_.isEqual(this.mutableSupplier, this.supplier)
    },

    isValidBillingAddress() {
      return Object.values(this.mutableSupplier.billingAddress).every(
        (value) => !!value
      )
    },

    userIsAllowedToEdit() {
      return this.isOwnerUser
    },

    validDetailsForm() {
      return this.formState && this.isNewData && this.isValidBillingAddress
    }
  },

  watch: {
    supplier: {
      immediate: true,
      handler() {
        if (this.editForm || !this.supplier) return
        this.resetForm()
      }
    }
  },

  methods: {
    ...mapActions('profile', ['fetchSupplierProfile']),
    ...mapActions('notifications', ['addNotification']),

    // ***
    // Functions related to Auto and Manual addresses
    // ***

    createDisplayAddress(address = undefined) {
      if (!address) {
        return null
      }

      const line1 = address.line1?.trim()
      const suburb = address.suburb?.trim()
      const state = address.state?.trim()
      const postCode = address.postCode?.trim()
      const locality = [suburb, [state, postCode].filter(Boolean).join(' ')]
        .filter(Boolean)
        .join(', ')
      const displayAddress = [line1, locality].filter(Boolean).join(', ')

      return displayAddress || null
    },

    handleManualBillingAddress() {
      this.manualBillingAddress = !this.manualBillingAddress
      if (!this.manualBillingAddress) {
        this.displayBillingAddress = null
        Object.assign(this.mutableSupplier.billingAddress, {
          line1: '',
          suburb: '',
          postCode: '',
          state: ''
        })
      }
    },

    onBillingAddressPlaceChanged(result) {
      this.displayBillingAddress = this.createDisplayAddress(result)

      Object.assign(this.mutableSupplier.billingAddress, {
        line1: result.line1,
        suburb: result.suburb,
        postCode: result.postCode,
        state: result.state
      })
    },

    // ***
    // Helper Functions
    // ***

    resetForm() {
      if (!this.supplier) {
        return
      }

      this.editForm = this.manualBillingAddress = false
      this.mutableSupplier = _.cloneDeep(this.supplier)
      this.displayBillingAddress = this.createDisplayAddress(
        this.mutableSupplier?.billingAddress
      )
    },

    submitForm() {
      this.updating = true

      const request = {
        accountContact: this.mutableSupplier.accountContact,
        businessContact: this.mutableSupplier.businessContact,
        billingAddress: this.mutableSupplier.billingAddress
      }

      updateProfile(this.selectedSupplierId, request)
        .then(() => {
          this.editForm = false
          this.addNotification({
            color: 'secondary',
            text: 'Account details successfully updated'
          })
          this.fetchSupplierProfile(this.selectedSupplierId)
        })
        .catch(() => {
          this.addNotification({ color: 'error', defaultError: true })
        })
        .finally(() => {
          this.updating = false
        })
    }
  },

  mounted() {
    this.resetForm()
  }
}
</script>

<style lang="scss" scoped>
#manage-details {
  hr {
    min-width: 100%;
    margin-top: 10px;
    margin-bottom: 30px;
  }
  .p-update-card {
    max-width: 740px;
  }
}
</style>
